import React, { useState, useEffect, createElement, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
  Progress,
  Stack,
  Textarea,
  Checkbox,
  Select,
  FormHelperText,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Switch,
  Badge,
  Divider,
  useToast,
  Alert,
  AlertIcon,
  IconButton,
  Link,
  InputLeftElement,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { createRegistry } from '../services/RegistryService';
import { useNavigate } from 'react-router-dom';
import * as SharingUtils from '../utils/sharingUtils';
import { mealTags, usStates } from '../utils/constants';
import MealPreferencesDialog from '../components/MealPreferencesDialog';
// Define a MotionBox component for the animation
const MotionFlex = motion(Flex);


// Get today's date in YYYY-MM-DD format for min date attribute
const today = new Date().toISOString().split('T')[0];

// Get a date 2 weeks from now as default due date
const defaultDueDate = new Date();
defaultDueDate.setDate(defaultDueDate.getDate() + 14);
const defaultDueDateFormatted = defaultDueDate.toISOString().split('T')[0];

interface FoodPreference {
  type: 'restaurant' | 'homemade' | 'local';
  name: string;
  specifics: string;
}

interface DeliveryDay {
  date: string;
  timeStart: string;
  timeEnd: string;
}

interface AddressData {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

interface AdminContact {
  name: string;
  relationship: string;
  email: string;
  phone: string;
}

interface FormData {
  parentName1: string;
  parentName2: string;
  parentNames: string; // Keeping for compatibility
  dueDate: string;
  email: string;
  address: AddressData;
  phoneNumber: string;
  adminContact: AdminContact;
  deliveryDays: DeliveryDay[];
  mealTags: Array<{ name: string; specifics: string }>;
  currentSignUps: Array<any>; // Will be empty during registration
  dislikes: string[];
  dietaryRestrictions: string;
  additionalNotes: string;
  selectedDates: string[];
  defaultTimeWindow: {
    start: string;
    end: string;
  };
  currentDeliveryDay: {
    date: string;
    timeStart: string;
    timeEnd: string;
  };
  currentFoodPref: {
    type: 'restaurant' | 'homemade' | 'local';
    name: string;
    specifics: string;
  };
  currentDislike: string;
  babyBorn: boolean;
  dateOfBirth: string;
  homeFromHospital: boolean;
  dateOfHomecoming: string;
  mealDays?: number;
  numberOfAdults?: number;
  numberOfKids?: number;
  adminPassword: string;
  confirmPassword: string;
  addressVisibilityAcknowledged: boolean;
  promotionalZipCode: string;
}

const Register = () => {
  const [step, setStep] = useState(1);
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isMealPreferencesOpen, setIsMealPreferencesOpen] = useState(false);
  const [confirmHasBeenBlurred, setConfirmHasBeenBlurred] = useState(false);
  const [registryId, setRegistryId] = useState('');
  const [customRestaurant, setCustomRestaurant] = useState('');
  const [customHomemade, setCustomHomemade] = useState('');
  const [showAdminSection, setShowAdminSection] = useState(false);
  const [hasSelectedAdmin, setHasSelectedAdmin] = useState(false);
  const [noAddressProvided, setNoAddressProvided] = useState(false);
  const { isOpen: isNoAddressDialogOpen, onOpen: onNoAddressDialogOpen, onClose: onNoAddressDialogClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const [noAddressDialogStep, setNoAddressDialogStep] = useState(1);
  const [noAddressZipCode, setNoAddressZipCode] = useState('');
  const [formData, setFormData] = useState<FormData>({
    parentName1: '',
    parentName2: '',
    parentNames: '',
    dueDate: defaultDueDateFormatted,
    email: '',
    address: {
      street: '',
      street2: '',
      city: '',
      state: '',
      zip: ''
    },
    phoneNumber: '',
    adminContact: {
      name: '',
      relationship: '',
      email: '',
      phone: ''
    },
    deliveryDays: [],
    mealTags: [],
    currentSignUps: [],
    dislikes: [],
    dietaryRestrictions: '',
    additionalNotes: '',
    selectedDates: [],
    defaultTimeWindow: {
      start: '17:00',
      end: '18:00'
    },
    currentDeliveryDay: {
      date: '',
      timeStart: '',
      timeEnd: ''
    },
    currentFoodPref: {
      type: 'homemade',
      name: '',
      specifics: ''
    },
    currentDislike: '',
    babyBorn: false,
    dateOfBirth: '',
    homeFromHospital: false,
    dateOfHomecoming: '',
    mealDays: 5,
    numberOfAdults: 2,
    numberOfKids: 0,
    adminPassword: '',
    confirmPassword: '',
    addressVisibilityAcknowledged: false,
    promotionalZipCode: '',
  });

  const totalSteps = 4;

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle nested objects
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      
      if (parent === 'currentDeliveryDay') {
        setFormData(prev => ({
          ...prev,
          currentDeliveryDay: {
            ...prev.currentDeliveryDay,
            [child]: value
          }
        }));
      } else if (parent === 'currentFoodPref') {
        setFormData(prev => ({
          ...prev,
          currentFoodPref: {
            ...prev.currentFoodPref,
            [child]: value
          }
        }));
      } else if (parent === 'address') {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            [child]: value
          }
        }));
      } else if (parent === 'adminContact') {
        setFormData(prev => ({
          ...prev,
          adminContact: {
            ...prev.adminContact,
            [child]: value
          }
        }));
      } else if (parent === 'defaultTimeWindow') {
        setFormData(prev => ({
          ...prev,
          defaultTimeWindow: {
            ...prev.defaultTimeWindow,
            [child]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Add this function after the existing utility functions (around line 536)
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);
    
    // Format as XXX-XXX-XXXX
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    } else {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
  };

  const handlePhoneNumberChange = (name: string, value: string) => {
    const formatted = formatPhoneNumber(value);
    
    if (name.includes('.')) {
      // Handle nested properties like "adminContact.phone"
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof FormData] as any,
          [child]: formatted
        }
      }));
    } else {
      // Handle direct properties like "phoneNumber"
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    }
  };

const getValidationMessage = () => {
  if (isStepValid()) return '';
  
  const missingFields: string[] = [];
  
  switch (step) {
    case 1:
      if (!formData.parentName1) missingFields.push('Parent 1 name');
      if (!formData.dueDate) missingFields.push('Due date');
      if (!formData.email) missingFields.push('Email address');
      if (!SharingUtils.validateEmail(formData.email)) {
        missingFields.push('Valid email');
      }
      if (!formData.phoneNumber || formData.phoneNumber.replace(/\D/g, '').length !== 10) {
        missingFields.push('Valid phone number');
      }
      if (!formData.adminPassword) missingFields.push('Password');
      if (!formData.confirmPassword) missingFields.push('Confirm password');
      if (!hasSelectedAdmin) missingFields.push('Activation Selection');
      
      if (showAdminSection) {
        if (!formData.adminContact.name) missingFields.push('Admin name');
        if (!formData.adminContact.relationship) missingFields.push('Admin relationship');
        if (!formData.adminContact.email) missingFields.push('Admin email');
        if (!SharingUtils.validateEmail(formData.adminContact.email)) {
          missingFields.push('Valid admin email');
        }
        if (!formData.adminContact.phone || formData.adminContact.phone.replace(/\D/g, '').length !== 10) {
          missingFields.push('Valid admin phone number');
        }
      }
      break;
      
    case 2:
      // Only validate address fields if address is being provided
      if (!noAddressProvided) {
        if (!formData.address.street) missingFields.push('Street address');
        if (!formData.address.city) missingFields.push('City');
        if (!formData.address.state) missingFields.push('State');
        if (!formData.address.zip) missingFields.push('ZIP code');
        if (!formData.address.zip.match(/^\d{5}$/)) missingFields.push('Valid ZIP code');
        if (!formData.addressVisibilityAcknowledged) missingFields.push('Delivery Address Acknowledgment');
      }
      
      // Always validate time window
      if (!formData.defaultTimeWindow.start) missingFields.push('Start time');
      if (!formData.defaultTimeWindow.end) missingFields.push('End time');
      if (formData.defaultTimeWindow.start && formData.defaultTimeWindow.end && 
          formData.defaultTimeWindow.start >= formData.defaultTimeWindow.end) {
        missingFields.push('End time must be after Start time');
      }
      break;
      
    default:
      return '';
  }
  if (missingFields.length === 1) {
    return `Please provide: ${missingFields[0]}`;
  } else if (missingFields.length > 1 && missingFields.length <= 3) {
    return `Please provide: ${missingFields.join(', ')}`;
  } else if (formData.adminPassword.length < 8) {
    return 'Password must be at least 8 characters long';
  } else if (formData.adminPassword && formData.confirmPassword && formData.adminPassword !== formData.confirmPassword) {
    return 'Passwords must match';
  } else {
    return `Please complete ${missingFields.length} required fields`;
  }
};

// Update the phone number input sections
  const handleNext = () => {
    if (!isStepValid()) {
      const validationMessage = getValidationMessage();
      
      // Check if we're on step 2 and address acknowledgment is missing
      if (step === 2 && !formData.addressVisibilityAcknowledged) {
        setHighlightAddressAcknowledgment(true);
        // Scroll to the checkbox
        setTimeout(() => {
          addressAcknowledgmentRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 100);
      }
      
      toast({
        title: validationMessage,
        description: '',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (step === 1) {
      // Email validation
      const emailError = formData.email ? !SharingUtils.validateEmail(formData.email) : '';
      const adminEmailError = formData.adminContact.email ? !SharingUtils.validateEmail(formData.adminContact.email) : '';
      
      // Show toasts for validation errors
      if (emailError) {
        toast({
          title: 'Invalid Email',
          description: 'Please enter a valid email address',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      if (adminEmailError) {
        toast({
          title: 'Invalid Admin Email',
          description: 'Please enter a valid email address for the administrator',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    // If moving from step 2 to step 3, fetch local restaurants by zip code


    if (isStepValid()) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isStepValid() && step === totalSteps) {
      try {
        setIsSubmitting(true);
        setError('');
        
        // If user chose not to add an administrator, set adminContact to parent's info
        const adminContactInfo = showAdminSection ? formData.adminContact : {
          name: formData.parentName1,
          relationship: "NoneChosen",
          email: formData.email,
          phone: formData.phoneNumber
        };
        
        // Ensure promotionalZipCode is populated
        let promotionalZipCode = formData.promotionalZipCode;
        if (!promotionalZipCode && formData.address.zip) {
          // If no promotional zip but address zip exists, copy it over
          promotionalZipCode = formData.address.zip;
        }
        
        // Clean up form data before submitting
        const registryData = {
          parentName1: formData.parentName1,
          parentName2: formData.parentName2,
          parentNames: formData.parentNames,
          dueDate: formData.dueDate,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          address: formData.address,
          adminContact: adminContactInfo,
          mealTags: formData.mealTags,
          currentSignUps: formData.currentSignUps,
          dislikes: formData.dislikes,
          dietaryRestrictions: formData.dietaryRestrictions,
          additionalNotes: formData.additionalNotes,
          defaultTimeWindow: formData.defaultTimeWindow,
          mealDays: formData.mealDays || 5,
          numberOfAdults: formData.numberOfAdults || 2,
          numberOfKids: formData.numberOfKids || 0,
          numberOfPeople: (formData.numberOfAdults || 2) + (formData.numberOfKids || 0),
          adminPassword: formData.adminPassword,
          promotionalZipCode: promotionalZipCode, // Include promotional zip code
        };
        
        // Create registry in DynamoDB
        const result = await createRegistry(registryData);
        
        // Store the registry ID
        setRegistryId(result.registryId);
        
        // Redirect to success page
        navigate(`/registry-success/${result.registryId}`);
      } catch (err) {
        console.error('Error creating registry:', err);
        setError('There was an error creating your MommaFuel. Please try again.');
        toast({
          title: 'Error',
          description: 'Failed to create MommaFuel. Please try again.',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Format address for display
  const getFormattedAddress = () => {
    const { street, street2, city, state, zip } = formData.address;
    let formatted = street;
    if (street2) formatted += ` ${street2}`;
    if (city || state || zip) formatted += `\n${city}, ${state} ${zip}`;
    return formatted;
  };
  // Format military time to 12-hour format with AM/PM
  const formatTime = (time: string): string => {
    if (!time) return '';
    
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Calculate if the form is valid for the current step
  const isStepValid = () => {
    switch (step) {
      case 1:
        const basicFieldsValid = formData.parentName1 && 
               formData.dueDate && 
               formData.email && 
               formData.phoneNumber &&
               formData.phoneNumber.length === 12 &&
               formData.adminPassword &&
               formData.confirmPassword &&
               formData.adminPassword === formData.confirmPassword &&
               SharingUtils.validateEmail(formData.email) &&
               hasSelectedAdmin;
        
        // Only validate admin fields if the section is shown
        if (showAdminSection) {
          return basicFieldsValid &&
                 formData.adminContact.name &&
                 formData.adminContact.relationship &&
                 formData.adminContact.email &&
                 SharingUtils.validateEmail(formData.adminContact.email) &&
                 formData.adminContact.phone &&
                 formData.adminContact.phone.length === 12;
        }
        
        return basicFieldsValid;
      
      case 2:
        // If no address is provided, skip address validation
        if (noAddressProvided) {
          return formData.defaultTimeWindow.start &&
                 formData.defaultTimeWindow.end &&
                 formData.defaultTimeWindow.start < formData.defaultTimeWindow.end;
        }
        
        // Otherwise require address fields and acknowledgment
        return formData.address.street && 
               formData.address.city && 
               formData.address.state && 
               formData.address.zip && 
               formData.address.zip.match(/^\d{5}$/) &&
               formData.defaultTimeWindow.start &&
               formData.defaultTimeWindow.end &&
               formData.defaultTimeWindow.start < formData.defaultTimeWindow.end &&
               formData.addressVisibilityAcknowledged;
      default:
        return true;
    }
  };

  const getNextStepName = () => {
    switch (step) {
      case 1:
        return 'Drop Off Details';
      case 2:
        return 'Meal Preferences';
      case 3:
        return 'Summary';
      default:
        return '';
    }
  };

  // Update parentNames whenever parentName1 or parentName2 changes
  useEffect(() => {
    let combined = formData.parentName1;
    if (formData.parentName2) {
      combined += ` & ${formData.parentName2}`;
    }
    
    if (combined !== formData.parentNames) {
      setFormData(prev => ({
        ...prev,
        parentNames: combined
      }));
    }
  }, [formData.parentName1, formData.parentName2]);

  const handleNoAddressChange = (checked: boolean) => {
    if (checked) {
      // Show confirmation dialog
      onNoAddressDialogOpen();
    } else {
      // Unchecking - reset to normal state
      setNoAddressProvided(false);
    }
  };

  const handleConfirmNoAddress = () => {
    if (noAddressDialogStep === 1) {
      // Move to step 2 (zip code input)
      setNoAddressDialogStep(2);
    } else {
      // Complete the process - step 2
      if (!noAddressZipCode || noAddressZipCode.length !== 5) {
        toast({
          title: 'Invalid Zip Code',
          description: 'Please enter a valid 5-digit zip code',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      
      // Set the local state and store promotional zip code
      setNoAddressProvided(true);
      setFormData(prev => ({
        ...prev,
        noAddressProvided: true,
        promotionalZipCode: noAddressZipCode,
        address: {
          ...prev.address,
          street: '',
          city: '',
          state: '',
          zip: ''
        }
      }));
      
      // Reset dialog state
      setNoAddressDialogStep(1);
      setNoAddressZipCode('');
      onNoAddressDialogClose();
    }
  };

  const handleCancelNoAddress = () => {
    // Reset dialog state when canceling
    setNoAddressDialogStep(1);
    setNoAddressZipCode('');
    onNoAddressDialogClose();
  };

  // Add these at the top of your component with other refs/state
  const addressAcknowledgmentRef = useRef<HTMLDivElement>(null);
  const [highlightAddressAcknowledgment, setHighlightAddressAcknowledgment] = useState(false);

  const formatZipCode = (value: string) => {
    return value.replace(/\D/g, '').slice(0, 5);
  };

  return (
    <Box minH="100vh" bg="mamaFuel.cream">
      <Container maxW="container.lg" py="10" px={{ base: "4", md: "6" }}>
        <VStack spacing="6" mb="8" textAlign="center">
          <Heading size="lg" color="mamaFuel.text.primary">Create Your MommaFuel</Heading>
        </VStack>

        <Box 
          bg="white" 
          p={{ base: "4", md: "8" }}
          borderRadius="lg" 
          boxShadow="md" 
          mb="8"
        >
          {/* Progress bar and steps */}
          <VStack mb="8" spacing="4" width="100%">
            <Box w="100%" position="relative">
              <Flex w="100%" position="relative">
                {/* Background Progress Line */}
                <Box 
                  position="absolute" 
                  top="5" 
                  left="0" 
                  right="0" 
                  height="2px" 
                  bg="gray.200" 
                  zIndex="1"
                />

                {/* Active Progress Line */}
                <Box 
                  position="absolute" 
                  top="5" 
                  left="0" 
                  width={
                    step === 1 ? "0%" :
                    step === 2 ? "33.33%" :
                    step === 3 ? "66.66%" :
                    "100%"
                  }
                  height="2px" 
                  bg="mamaFuel.moss.700" 
                  zIndex="1"
                />
                
                {/* Step Circles */}
                <Flex w="100%" justify="space-between" position="relative" zIndex="2">
                  {[1, 2, 3, 4].map((stepNumber) => (
                    <Box
                      key={stepNumber}
                      w="10"
                      h="10"
                      borderRadius="full"
                      bg={step >= stepNumber ? "mamaFuel.moss.700" : "gray.200"}
                      color={step >= stepNumber ? "white" : "gray.400"}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      mr={stepNumber === 4 ? "0" : undefined}
                    >
                      {stepNumber}
                    </Box>
                  ))}
                </Flex>
              </Flex>
              
            </Box>
        </VStack>

        <Box
          as="form"
          onSubmit={handleSubmit}
          bg="white"
          p={{ base: "4", md: "8" }}
          rounded="lg"
          shadow="base"
        >
          {/* Step 1: Basic Information */}
          {step === 1 && (
            <VStack spacing="6">
              <Box width="100%" textAlign="center" mb="4">
                <Text fontSize="3rem" lineHeight="1" mb="2">
                  👶
                </Text>
                  <Heading size="md" mt="2" color="mamaFuel.text.primary">Family Information</Heading>
                <Text mt="2">Tell us about your growing family so we can set up your meal support system</Text>
              </Box>
            
              <FormControl isRequired>
                  <Stack 
                    spacing={4}
                    direction={["column", "row"]} 
                    align={["stretch", "flex-start"]}
                    width="100%"
                  >
                    <FormControl isRequired flex={1}>
                    <FormLabel>Parent 1</FormLabel>
                    <Input
                      name="parentName1"
                      value={formData.parentName1}
                      onChange={(e) => {
                        // Only allow letters and spaces
                        const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                        setFormData(prev => ({
                          ...prev,
                          parentName1: value
                        }));
                      }}
                      onBlur={(e) => {
                        // Capitalize first letter when field loses focus
                        if (formData.parentName1) {
                          const capitalized = formData.parentName1.charAt(0).toUpperCase() + formData.parentName1.slice(1);
                          setFormData(prev => ({
                            ...prev,
                            parentName1: capitalized
                          }));
                        }
                      }}
                    />
                  </FormControl>
                    <FormControl flex={1}>
                    <FormLabel>Parent 2</FormLabel>
                    <Input
                      name="parentName2"
                      value={formData.parentName2}
                      onChange={(e) => {
                        // Only allow letters and spaces
                        const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                        setFormData(prev => ({
                          ...prev,
                          parentName2: value
                        }));
                      }}
                      onBlur={(e) => {
                        // Capitalize first letter when field loses focus
                        if (formData.parentName2) {
                          const capitalized = formData.parentName2.charAt(0).toUpperCase() + formData.parentName2.slice(1);
                          setFormData(prev => ({
                            ...prev,
                            parentName2: capitalized
                          }));
                        }
                      }}
                    />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel>Expected Due Date</FormLabel>
                    <Input
                      name="dueDate"
                      type="date"
                      value={formData.dueDate}
                      onChange={handleInputChange}
                      min={today}
                      onBlur={(e) => {
                        // Ensure date is not before today
                        if (e.target.value < today) {
                          setFormData(prev => ({
                            ...prev,
                            dueDate: today
                          }));
                        }
                      }}
                    />
                  </FormControl>
                </Stack>
              </FormControl>
              
              <Stack direction={["column", "row"]} spacing={4} align={["stretch", "flex-start"]} width="100%">
                <FormControl isRequired flex={1}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </FormControl>
                
                <FormControl isRequired flex={1}>
                  <FormLabel>Phone Number</FormLabel>
                  <InputGroup>
                    <InputLeftElement 
                      pointerEvents="none" 
                      color="gray.500" 
                      fontSize="sm"
                      pl={2}
                    >
                      +1
                    </InputLeftElement>
                    <Input
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handlePhoneNumberChange('phoneNumber', e.target.value)}
                      pl={10}
                      minLength={12}
                      maxLength={12}
                      onKeyDown={SharingUtils.onlyAllowDigits}
                    />
                  </InputGroup>
                </FormControl>
              </Stack>
              
              <Divider mt="4" />
              
              <Box width="100%" textAlign="center" mb="2" mt="2">
                <Text fontSize="3rem" lineHeight="1" mb="2">
                  👥
                </Text>
                <Heading size="md" mt="2" color="mamaFuel.text.primary">MommaFuel Activation</Heading>
                <Text fontWeight="bold" mt="2">
                Your MommaFuel will be pending until the baby arrives. When the baby arrives and you know when you're returning home, someone will then need to activate the MommaFuel and choose the first meal date. Select who will be responsible for this:                
                </Text>
                <VStack spacing={4} mt={6} width="full">
                  <Button 
                    colorScheme="blue" 
                    width="full" 
                    onClick={() => {
                      setShowAdminSection(false);
                      setHasSelectedAdmin(true);
                    }}
                    height="auto"
                    py={3}
                    textAlign="center"
                    variant={!hasSelectedAdmin ? "outline" : (showAdminSection ? "outline" : "primary")}
                    borderColor={!hasSelectedAdmin ? "gray.300" : (showAdminSection ? "" : "black")}
                    borderWidth={!hasSelectedAdmin ? "1px" : (showAdminSection ? "0" : "1px")}
                  >
                    I will activate the MommaFuel myself
                  </Button>
                  
                  <Button 
                    colorScheme="blue" 
                    width="full" 
                    onClick={() => {
                      setShowAdminSection(true);
                      setHasSelectedAdmin(true);
                    }}
                    variant={!hasSelectedAdmin ? "outline" : (!showAdminSection ? "outline" : "primary")}
                    borderColor={!hasSelectedAdmin ? "gray.300" : (!showAdminSection ? "" : "black")}
                    borderWidth={!hasSelectedAdmin ? "1px" : (!showAdminSection ? "0" : "1px")}
                    whiteSpace="normal"
                    height="auto"
                    py={3}
                    textAlign="center"
                  >
                    I would like to add an additional Administrator who can also activate it
                  </Button>
                </VStack>
              </Box>
              
              {showAdminSection && (
                <>
                  <Box width="100%" textAlign="center" mb="2" mt="6">
                    <Heading size="md" mt="2" color="mamaFuel.text.primary">MommaFuel Administrator</Heading>
                    <Text mt="2">This person will update your MommaFuel when the baby arrives and set the first meal date. It should be someone who will know the status of the baby and the couple's plans to return home. We recommend a family member or close friend- not a parent of the baby.</Text>
                  </Box>
                  
                  <Stack direction={["column", "row"]} spacing={4} align={["stretch", "flex-start"]} width="100%">
                    <FormControl isRequired={showAdminSection} flex={1}>
                      <FormLabel>Name</FormLabel>
                      <Input
                        name="adminContact.name"
                        value={formData.adminContact.name}
                        onChange={handleInputChange}
                      />
                    </FormControl>
                    
                    <FormControl isRequired={showAdminSection} flex={1}>
                      <FormLabel>Relationship</FormLabel>
                      <Input
                        name="adminContact.relationship"
                        value={formData.adminContact.relationship}
                        onChange={handleInputChange}
                        placeholder="e.g., Sister, Grandmother, Friend"
                      />
                    </FormControl>
                  </Stack>
                  
                  <Stack direction={["column", "row"]} spacing={4} align={["stretch", "flex-start"]} width="100%">  
                    <FormControl isRequired={showAdminSection} flex={1}>
                      <FormLabel>Email</FormLabel>
                      <Input
                        name="adminContact.email"
                        type="email"
                        value={formData.adminContact.email}
                        onChange={handleInputChange}
                      />
                    </FormControl>
                    
                    <FormControl isRequired={showAdminSection} flex={1}>
                      <FormLabel>Phone Number</FormLabel>
                      <InputGroup>
                        <InputLeftElement 
                          pointerEvents="none" 
                          color="gray.500" 
                          fontSize="sm"
                          pl={2}
                        >
                          +1
                        </InputLeftElement>
                        <Input
                          name="adminContact.phone"
                          type="tel"
                          value={formData.adminContact.phone}
                          onChange={(e) => handlePhoneNumberChange('adminContact.phone', e.target.value)}
                          pl={10}
                          minLength={12}
                          maxLength={12}
                          onKeyDown={SharingUtils.onlyAllowDigits}
                        />
                      </InputGroup>
                    </FormControl>
                  </Stack>
                </>
              )}

              <Divider mt="4" />

              <Box width="100%" textAlign="center" mt="2">
                <Text fontSize="3rem" lineHeight="1" mb="2">
                  🔐
                </Text>
                  <Heading size="md" mt="2" color="mamaFuel.text.primary">MommaFuel Security</Heading>
                <Text mt="2">Password is used to edit the MommaFuel.</Text>
                <Text fontSize="xs" mt="2">Must be at least 8 characters.</Text>
              </Box>
              
              <Stack direction={["column", "row"]} spacing={4} align={["stretch", "flex-start"]} width="100%">  
                <FormControl isRequired>
                  <FormLabel>Password</FormLabel>
                  <Input
                    name="adminPassword"
                    type={showPasswords ? "text" : "password"}
                    value={formData.adminPassword}
                    onChange={handleInputChange}
                    minLength={8}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Confirm Password</FormLabel>
                  <Input
                    name="confirmPassword"
                    type={showPasswords ? "text" : "password"}
                    minLength={8}
                    value={formData.confirmPassword}
                    onChange={(e) => {
                      if (e.target.value == '' || e.target.value == null) {
                        setConfirmHasBeenBlurred(false);
                      }
                      handleInputChange(e);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setConfirmHasBeenBlurred(true);
                        handleNext();
                      }
                    }}
                    onFocus={() => {
                      setConfirmHasBeenBlurred(false);
                    }}
                    onBlur={() => {
                      setConfirmHasBeenBlurred(true);
                    }}
                  />
                </FormControl>
              </Stack>

              <Checkbox 
                isChecked={showPasswords}
                alignSelf="flex-start"
                colorScheme="green"
                onChange={(e) => setShowPasswords(e.target.checked)}
              >
                Show Passwords
              </Checkbox>

            </VStack>
          )}

          {/* Step 2: Delivery Scheduling */}
          {step === 2 && (
            <VStack spacing="6" alignItems="flex-start">
            
              <Box width="100%">
                <Text fontSize="lg" fontWeight="bold">Where would you like your meals delivered?</Text>
              </Box>
              
              {/* Only show address visibility acknowledgment if address is provided */}
              {!noAddressProvided && (
                <FormControl isRequired ref={addressAcknowledgmentRef}>
                  <Checkbox
                    isChecked={formData.addressVisibilityAcknowledged}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        addressVisibilityAcknowledged: e.target.checked
                      }));
                      // Clear highlight when user checks the box
                      if (e.target.checked) {
                        setHighlightAddressAcknowledgment(false);
                      }
                    }}
                    colorScheme="green"
                    sx={{
                      '& .chakra-checkbox__control': {
                        borderColor: 'gray.800',
                        borderWidth: '2px'
                      }
                    }}
                  >
                    <Text 
                      fontSize="md"
                      color={highlightAddressAcknowledgment ? "red.500" : "inherit"}
                    >
                      I understand: anyone with the MommaFuel link will be able to see this delivery address
                    </Text>
                  </Checkbox>
                </FormControl>
              )}

              <FormControl>
                <Checkbox
                  isChecked={noAddressProvided}
                  onChange={(e) => handleNoAddressChange(e.target.checked)}
                  colorScheme="green"
                  mt={-4}
                  sx={{
                    '& .chakra-checkbox__control': {
                      borderColor: 'gray.800',
                      borderWidth: '2px'
                    }
                  }}
                >
                  <Text fontSize="md">
                    I'd prefer not to provide an address
                  </Text>
                </Checkbox>
              </FormControl>
              
              <Flex direction={{ base: "column", md: "row" }} width="100%" gap="4" mt={-2}>
                <FormControl isRequired={!noAddressProvided}>
                  <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>Street Address</FormLabel>
                  <Input
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    isDisabled={noAddressProvided}
                    bg={noAddressProvided ? "gray.100" : "white"}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>Apartment, Suite, etc.</FormLabel>
                  <Input
                    name="address.street2"
                    value={formData.address.street2}
                    onChange={handleInputChange}
                    isDisabled={noAddressProvided}
                    bg={noAddressProvided ? "gray.100" : "white"}
                  />
                </FormControl>
              </Flex>
              
              <Flex direction={{ base: "column", md: "row" }} width="100%" gap="4">
                <FormControl isRequired={!noAddressProvided} flex="1">
                  <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>City</FormLabel>
                  <Input
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleInputChange}
                    isDisabled={noAddressProvided}
                    bg={noAddressProvided ? "gray.100" : "white"}
                  />
                </FormControl>
                
                <FormControl isRequired={!noAddressProvided} flex="1">
                  <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>State</FormLabel>
                  <Select
                    name="address.state"
                    border="1px solid"
                    borderColor="black"
                    placeholder="--"
                    value={formData.address.state}
                    onChange={handleInputChange}
                    isDisabled={noAddressProvided}
                    bg={noAddressProvided ? "gray.100" : "white"}
                  >
                    {usStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl isRequired={!noAddressProvided} flex="1">
                  <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>ZIP Code</FormLabel>
                  <Input
                    name="address.zip"
                    value={formData.address.zip}
                    onChange={handleInputChange}
                    maxLength={5}
                    onKeyDown={SharingUtils.onlyAllowDigits}
                    isDisabled={noAddressProvided}
                    bg={noAddressProvided ? "gray.100" : "white"}
                  />
                </FormControl>
              </Flex>

              <Divider />

              <Box width="100%" mt="6">
                <Text fontSize="lg" fontWeight="bold">Meal Specifics</Text>
              </Box>
              
              <VStack width="100%" spacing="4" alignItems="flex-start" p="4" pt="0" borderRadius="md">
                <Flex direction={{ base: "column", md: "row" }} width="100%" gap="4">
                  <FormControl flex="1">
                    <FormLabel>How many days of meals?</FormLabel>
                    <Select 
                      name="mealDays"
                      border="1px solid"
                      borderColor="black"
                      value={formData.mealDays?.toString() || "5"}
                      onChange={(e) => {
                        // Store the number of meal days in the form data
                        setFormData(prev => ({
                          ...prev,
                          mealDays: parseInt(e.target.value, 10)
                        }));
                      }}
                    >
                      <option value="1">1 day</option>
                      <option value="2">2 days</option>
                      <option value="3">3 days</option>
                      <option value="4">4 days</option>
                      <option value="5">5 days</option>
                      <option value="6">6 days</option>
                      <option value="7">7 days</option>
                      <option value="8">8 days</option>
                      <option value="9">9 days</option>
                      <option value="10">10 days</option>
                      <option value="11">11 days</option>
                      <option value="12">12 days</option>
                      <option value="13">13 days</option>
                      <option value="14">14 days</option>
                      <option value="15">15 days</option>
                      <option value="16">16 days</option>
                      <option value="17">17 days</option>
                      <option value="18">18 days</option>
                      <option value="19">19 days</option>
                      <option value="20">20 days</option>
                      <option value="21">21 days</option>
                    </Select>
                  </FormControl>
                  
                  <FormControl flex="1">
                    <FormLabel>How many adults are eating?</FormLabel>
                    <Select
                      name="numberOfAdults"
                      border="1px solid"
                      borderColor="black"
                      value={formData.numberOfAdults?.toString() || "2"}
                      onChange={(e) => {
                        // Store the number of adults in the form data
                        setFormData(prev => ({
                          ...prev,
                          numberOfAdults: parseInt(e.target.value, 10)
                        }));
                      }}
                    >
                      <option value="1">1 adult</option>
                      <option value="2">2 adults</option>
                      <option value="3">3 adults</option>
                      <option value="4">4 adults</option>
                      <option value="5">5 adults</option>
                      <option value="6">6 adults</option>
                      <option value="7">7 adults</option>
                      <option value="8">8+ adults</option>
                    </Select>
                  </FormControl>
                  
                  <FormControl flex="1">
                    <FormLabel>How many children are eating?</FormLabel>
                    <Select
                      name="numberOfKids"
                      border="1px solid"
                      borderColor="black"
                      value={formData.numberOfKids?.toString() || "0"}
                      onChange={(e) => {
                        // Store the number of kids in the form data
                        setFormData(prev => ({
                          ...prev,
                          numberOfKids: parseInt(e.target.value, 10)
                        }));
                      }}
                    >
                      <option value="0">0 children</option>
                      <option value="1">1 child</option>
                      <option value="2">2 children</option>
                      <option value="3">3 children</option>
                      <option value="4">4 children</option>
                      <option value="5">5 children</option>
                      <option value="6">6 children</option>
                      <option value="7">7+ children</option>
                    </Select>
                  </FormControl>
                </Flex>
              </VStack>

              <Divider mt="4" />
                
              <FormControl isRequired>
                <Box width="100%" mt="6" mb="6">
                  <Text fontSize="lg" fontWeight="bold">What time window should meals be delivered?</Text>
                </Box>
                <Flex direction={{ base: "column", md: "row" }} width="100%" gap="4">
                  <FormControl isRequired flex="1">
                  <Text fontWeight="medium" align="start" mb={2}>Start Time</Text>
                    <Input
                      name="defaultTimeWindow.start"
                      type="time"
                      value={formData.defaultTimeWindow.start}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                      
                  <FormControl isRequired flex="1">
                    <Text fontWeight="medium" align="start" mb={2}>End Time</Text>
                    <Input
                      name="defaultTimeWindow.end"
                      type="time"
                      value={formData.defaultTimeWindow.end}
                      onChange={handleInputChange}
                      onKeyDown={
                        (e) => {
                          if (e.key === "Enter") {
                            handleNext();
                          }
                        }
                      }
                    />
                  </FormControl>
                </Flex>
              </FormControl>
              <Divider />
              <FormControl>
                <FormLabel>Additional Delivery Instructions</FormLabel>
                <Textarea
                  value={formData.additionalNotes}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      additionalNotes: e.target.value
                    }));
                  }}
                  placeholder="Doorbell doesn't work, please text when arriving, etc."
                  rows={5}
                />
              </FormControl>
            </VStack>
          )}

          {/* Step 3: Food Preferences */}
          {step === 3 && (
            <VStack spacing="6">
              <Box width="100%" textAlign="center">
                <Text fontSize="3rem" lineHeight="1" mb="2">
                  🍽️
                </Text>
                <Heading size="md" mt="2" color="mamaFuel.text.primary">Meal Preferences</Heading>
              </Box>

              <Text alignSelf="flex-start">
                Your friends will deliver a dinner of their choosing!  However, if you'd like to add suggestions - click here!
              </Text>

              {/* Show selected preferences count */}
              {formData.mealTags.length > 0 && (
                <Box 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="md" 
                  width="100%"
                >

                  <SimpleGrid spacing="0" columns={{ base: 2, md: 3, lg: 4}}>
                    {formData.mealTags.map((pref, i) => (
                      <Text key={i} fontSize="sm" fontWeight="medium">
                        {mealTags.find(tag => tag.name === pref.name)?.icon || "🏷️"} {pref.name}
                      </Text>
                    ))}
                  </SimpleGrid>
                </Box>
              )}

              <Button
                bg="mamaFuel.moss.700"
                color="white"
                _hover={{ bg: "mamaFuel.moss.600" }}
                size="lg"
                onClick={() => setIsMealPreferencesOpen(true)}
              >
                {formData.mealTags.length > 0 ? 'Edit Meal Suggestions' : 'Add Meal Suggestions'}
              </Button>

              {/* Meal Preferences Dialog */}
              <MealPreferencesDialog
                isOpen={isMealPreferencesOpen}
                onClose={() => setIsMealPreferencesOpen(false)}
                formData={formData}
                setFormData={setFormData}
              />

            <FormControl>
              <FormLabel>Foods you'd prefer not to receive</FormLabel>
              <Textarea
                value={formData.dislikes.join(', ')}
                onChange={(e) => {
                  const dislikesText = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    dislikes: dislikesText ? [dislikesText] : []
                  }));
                }}
                rows={3}
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>Dietary Restrictions or Special Needs</FormLabel>
              <Textarea
                value={formData.dietaryRestrictions}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    dietaryRestrictions: e.target.value
                  }));
                }}
                placeholder="Vegetarian, vegan, gluten-free, etc."
                rows={3}
              />
            </FormControl>
            </VStack>
          )}


          {/* Step 4: Final Details */}
          {step === 4 && (
            <VStack spacing="6" align="stretch">
              
              <Box width="100%" p="6" borderWidth="1px" borderRadius="lg">
                <Heading size="md" mb="4">MommaFuel Summary</Heading>
                
                <VStack spacing="4" alignItems="flex-start">
                  <Box width="100%">
                    <Text fontWeight="bold">{formData.parentNames.length > 1 ? "Parents:" : "Parent:"}</Text>
                    <Text>{formData.parentNames || "Not provided"}</Text>
                    <Text>{formData.email || ""}</Text>
                    <Text>+1 {formData.phoneNumber || ""}</Text>
                  </Box>
                  <Box width="100%">
                    <Text fontWeight="bold">Expected Due Date:</Text>
                    <Text>{formData.dueDate.split('T')[0].replace(/(\d{4})-(\d{2})-(\d{2})/, (_, year, month, day) => {
                      const date = new Date(year, parseInt(month) - 1, parseInt(day));
                      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                    })}</Text>
                  </Box>
                  
                  {formData.adminContact.name && (
                    <Box width="100%">
                      <Text fontWeight="bold">MommaFuel Administrator:</Text>
                      <Text>{formData.adminContact.name} ({formData.adminContact.relationship})</Text>
                      <Text>{formData.adminContact.email} | {formData.adminContact.phone}</Text>
                    </Box>
                  )}

                  <Box width="100%" >
                    <Text fontWeight="bold">Delivery Address:</Text>
                    <Text whiteSpace="pre-line">{getFormattedAddress() || "Not provided"}</Text>
                  </Box>
                  
                  <Box width="100%">
                    <Text fontWeight="bold">Meal Details:</Text>
                    <Text>{formData.mealDays || 5} days of meals for {formData.numberOfAdults || 2} {formData.numberOfAdults === 1 ? 'adult' : 'adults'} and {formData.numberOfKids || 0} {formData.numberOfKids === 1 ? 'child' : 'children'}</Text>
                  </Box>

                  <Box width="100%">
                    <Text fontWeight="bold">Delivery Time Window:</Text>
                    <Text>{formatTime(formData.defaultTimeWindow.start)} to {formatTime(formData.defaultTimeWindow.end)}</Text>
                  </Box>
                  
                  <Box width="100%" textAlign="center">
                    <Text fontWeight="bold">Meal Suggestions:</Text>
                    {formData.mealTags.length > 0 ? (
                      <VStack spacing="1" alignItems="center" width="100%">
                        {formData.mealTags.map((pref, i) => (
                          <Text key={i}>
                            {mealTags.find(tag => tag.name === pref.name)?.icon || "🏷️"} {pref.name}
                          </Text>
                        ))}
                      </VStack>
                    ) : (
                      <Text>No meal suggestions added</Text>
                    )}
                  </Box>
                  
                  {formData.dislikes.length > 0 && (
                    <Box width="100%" textAlign="center">
                      <Text fontWeight="bold">Foods you'd prefer not to receive:</Text>
                      <Text>{formData.dislikes.join(', ')}</Text>
                    </Box>
                  )}
                  
                  {formData.dietaryRestrictions && (
                    <Box width="100%" textAlign="center">
                      <Text fontWeight="bold">Dietary Restrictions or Special Needs:</Text>
                      <Text whiteSpace="pre-line">{formData.dietaryRestrictions}</Text>
                    </Box>
                  )}

                  {formData.additionalNotes && (
                    <Box width="100%" textAlign="center">
                      <Text fontWeight="bold">Additional Delivery Instructions:</Text>
                      <Text whiteSpace="pre-line">{formData.additionalNotes}</Text>
                    </Box>
                  )}
                </VStack>
              </Box>
              
              {error && (
                  <Alert status="error" mb="4" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              
                <Text fontSize="sm" color="mamaFuel.moss.700">
                You'll receive an email with your unique MommaFuel link to share with friends and family.
              </Text>
            </VStack>
          )}


          <HStack w="full" justify="space-between" mt="8">
            {step > 1 && (
              <Button 
                onClick={handleBack} 
                variant="outline" 
                borderColor="mamaFuel.moss.700"
                color="mamaFuel.moss.700"
                _hover={{ bg: 'mamaFuel.cream' }}
              >
                Back
              </Button>
            )}
            
            <VStack flex="1" align="flex-end" spacing={1}>          
            {step < totalSteps ? (
                <Button
                  onClick={handleNext} 
                  bg="mamaFuel.moss.700" 
                  color="white"
                  _hover={{ bg: "mamaFuel.moss.600" }}
                >
                  Next: {getNextStepName()}
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  bg="mamaFuel.moss.700" 
                  color="white"
                  _hover={{ bg: "mamaFuel.moss.600" }}
                  isLoading={isSubmitting}
                  loadingText="Creating MommaFuel"
                >
                  Create MommaFuel
                </Button>
              )}
            </VStack>
          </HStack>
        </Box>
        </Box>
    </Container>

    {/* No Address Confirmation Dialog */}
    <AlertDialog
      isOpen={isNoAddressDialogOpen}
      leastDestructiveRef={cancelRef as React.RefObject<any>}
      onClose={onNoAddressDialogClose}
      onCloseComplete={() => {
        // Reset dialog state when dialog closes
        setNoAddressDialogStep(1);
        setNoAddressZipCode('');
      }}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          {noAddressDialogStep === 1 ? (
            <>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                No Address Confirmation
              </AlertDialogHeader>
              <AlertDialogBody>
                Check this box if you don't want any delivery address to be shown. Only do this if everyone bringing meals already knows where to go!
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={handleCancelNoAddress}>
                  Cancel
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={handleConfirmNoAddress} 
                  ml={3}
                >
                  Continue
                </Button>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Zip Code for Promotions
              </AlertDialogHeader>
              <AlertDialogBody>
                <VStack spacing={4} align="stretch">
                  <Text>
                    To provide restaurant deals and promotions to people signing up for meals in your area, please enter your zip code.
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Note:</strong> This zip code will not be displayed to others - it's only used to show relevant local restaurant promotions when people sign up to bring meals.
                  </Text>
                  <FormControl isRequired>
                    <FormLabel>Zip Code</FormLabel>
                    <Input
                      type="text"
                      value={noAddressZipCode}
                      onChange={(e) => setNoAddressZipCode(formatZipCode(e.target.value))}
                      maxLength={5}
                      autoFocus
                    />
                  </FormControl>
                </VStack>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button onClick={() => setNoAddressDialogStep(1)}>
                  Back
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={handleConfirmNoAddress} 
                  ml={3}
                >
                  Confirm
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </Box>
  );
};

export default Register; 